/**
 * Crops a PNG to the bounding box of non-transparent / non-near-white ink.
 * Removes large empty margins that make signatures look oversized in Word/PDF.
 */
export async function trimPngWhitespace(
  input: Blob | ArrayBuffer | Uint8Array,
  options?: { alphaThreshold?: number; whiteThreshold?: number; padding?: number },
): Promise<Uint8Array> {
  const alphaThreshold = options?.alphaThreshold ?? 8;
  const whiteThreshold = options?.whiteThreshold ?? 245;
  const padding = options?.padding ?? 4;

  const bytes =
    input instanceof Uint8Array
      ? input
      : input instanceof ArrayBuffer
        ? new Uint8Array(input)
        : new Uint8Array(await input.arrayBuffer());

  const blob = new Blob([new Uint8Array(bytes)], { type: 'image/png' });
  const bitmap = await createImageBitmap(blob);
  const w = bitmap.width;
  const h = bitmap.height;
  if (w < 1 || h < 1) {
    bitmap.close();
    return bytes;
  }

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) {
    bitmap.close();
    return bytes;
  }
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();

  const { data } = ctx.getImageData(0, 0, w, h);
  let minX = w;
  let minY = h;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const a = data[i + 3];
      if (a < alphaThreshold) continue;
      // Treat near-white as empty background
      if (r >= whiteThreshold && g >= whiteThreshold && b >= whiteThreshold) continue;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }

  if (maxX < minX || maxY < minY) {
    return bytes;
  }

  minX = Math.max(0, minX - padding);
  minY = Math.max(0, minY - padding);
  maxX = Math.min(w - 1, maxX + padding);
  maxY = Math.min(h - 1, maxY + padding);

  const cropW = maxX - minX + 1;
  const cropH = maxY - minY + 1;

  // Already tight enough (less than ~8% waste on each side)
  const waste = 1 - (cropW * cropH) / (w * h);
  if (waste < 0.08 && minX <= padding && minY <= padding && maxX >= w - 1 - padding && maxY >= h - 1 - padding) {
    return bytes;
  }

  const out = document.createElement('canvas');
  out.width = cropW;
  out.height = cropH;
  const octx = out.getContext('2d');
  if (!octx) return bytes;
  octx.drawImage(canvas, minX, minY, cropW, cropH, 0, 0, cropW, cropH);

  const outBlob = await new Promise<Blob | null>((resolve) =>
    out.toBlob((b) => resolve(b), 'image/png'),
  );
  if (!outBlob) return bytes;
  return new Uint8Array(await outBlob.arrayBuffer());
}
